import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import Shell from "@/components/Shell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Production from "@/pages/Production";
import Dispatch from "@/pages/Dispatch";
import TruckDetail from "@/pages/TruckDetail";
import Distributors from "@/pages/Distributors";
import Products from "@/pages/Products";
import Users from "@/pages/Users";
import Reports from "@/pages/Reports";
import ActivityLogs from "@/pages/ActivityLogs";
import Settings from "@/pages/Settings";
import SettingsRoles from "@/pages/SettingsRoles";
import FGStock from "@/pages/FGStock";
import Formulas from "@/pages/Formulas";
import SKUMaster from "@/pages/SKUMaster";
import FGClosingStock from "@/pages/FGClosingStock";
import NewPFI from "@/pages/NewPFI";
import Profile from "@/pages/Profile";
import UserPermissions from "@/pages/UserPermissions";
import Territories from "@/pages/Territories";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/** Route-level role guard. Keeps the RBAC matrix in a single place so direct-URL
 *  access cannot bypass the sidebar gating. Redirects forbidden roles to "/". */
function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

/** Phase 4 — permission-aware route guard. Reads `effective_permissions.modules`
 *  if present; falls back to role check otherwise (backward compat).
 *  Redirects to "/" when the user's permissions don't include the module. */
function RequireModule({ module, fallbackRoles = [], children }) {
  const { user, hasModule } = useAuth();
  if (!user) return null;
  if (user.effective_permissions?.modules) {
    if (!hasModule(module)) return <Navigate to="/" replace />;
  } else if (fallbackRoles.length && !fallbackRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  useEffect(() => { document.title = "XTRA-OMS"; }, []);
  return (
    <AuthProvider>
      <BrandingProvider>
        <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Protected><Shell /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="production" element={<RequireModule module="production" fallbackRoles={["super_admin","admin","production","management","operations"]}><Production /></RequireModule>} />
            <Route path="dispatch" element={<RequireModule module="dispatch" fallbackRoles={["super_admin","admin","dispatch","management","operations"]}><Dispatch /></RequireModule>} />
            <Route path="dispatch/trucks/:id" element={<RequireModule module="dispatch" fallbackRoles={["super_admin","admin","dispatch","management","operations"]}><TruckDetail /></RequireModule>} />
            <Route path="distributors" element={<RequireModule module="distributors" fallbackRoles={["super_admin","admin","pfi","accounts","management","operations","sales"]}><Distributors /></RequireModule>} />
            <Route path="products" element={<RequireModule module="products" fallbackRoles={["super_admin","admin","pfi","management","operations","sales"]}><Products /></RequireModule>} />
            <Route path="users" element={<RequireRole roles={["super_admin"]}><Users /></RequireRole>} />
            <Route path="users/permissions/:id" element={<RequireRole roles={["super_admin"]}><UserPermissions /></RequireRole>} />
            <Route path="territories" element={<Territories />} />
            <Route path="reports" element={<RequireModule module="reports" fallbackRoles={["super_admin","admin","accounts","management","operations"]}><Reports /></RequireModule>} />
            <Route path="activity" element={<RequireModule module="activity_logs" fallbackRoles={["super_admin","admin","management"]}><ActivityLogs /></RequireModule>} />
            <Route path="settings" element={<RequireModule module="settings" fallbackRoles={["super_admin"]}><Settings /></RequireModule>} />
            <Route path="settings/roles" element={<RequireRole roles={["super_admin"]}><SettingsRoles /></RequireRole>} />
            <Route path="fg-stock" element={<RequireModule module="fg_stock" fallbackRoles={["super_admin","admin","production","management","operations"]}><FGStock /></RequireModule>} />
            <Route path="formulas" element={<RequireModule module="formulas" fallbackRoles={["super_admin","admin","production","management","operations"]}><Formulas /></RequireModule>} />
            <Route path="sku-master" element={<RequireModule module="sku_master" fallbackRoles={["super_admin","admin","operations","inventory_executive"]}><SKUMaster /></RequireModule>} />
            <Route path="inventory/closing-stock" element={<RequireModule module="fg_closing_stock" fallbackRoles={["super_admin","admin","operations","inventory_executive"]}><FGClosingStock /></RequireModule>} />
            <Route path="pfi/new" element={<RequireModule module="new_pfi" fallbackRoles={["super_admin","admin","pfi","operations","sales"]}><NewPFI /></RequireModule>} />
            <Route path="profile" element={<Profile />} />
          </Route>
        </Routes>
        </BrowserRouter>
      </BrandingProvider>
    </AuthProvider>
  );
}

export default App;
